import axios, { AxiosResponse } from 'axios';
import { useContext, useState } from 'react';
import { Company, User } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { Grid, Typography } from '@mui/material';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import ContactFormTextInput from './ContactFormTextInput';
import ContactFormSelect from './ContactFormSelect';
import { API_ENDPOINT } from '../../env';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import PrimaryButton from '../shared/button/PrimaryButton';
import BorderContainer from '../shared/container/BorderContainer';
import { validateEmail } from '../../lib/validation/text_validation';

interface ContactFormProps {
  user: User | null;
  company: Company | null;
}

interface ContactFormDetails {
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone_number: string;
  application_area: string;
  message: string;
}

interface ContactRequestBody extends ContactFormDetails {
  request_inbox: string;
}

/**
 * Generic contact form to be used for internal and external user contact requests.
 * @param {User | null} user Logged in user making the contact request, null if logged out contact state.
 * @param {Company | null} company Logged in company context of user making request, null if logged out contact state.
 * @returns {JSX.Element}
 */
const ContactForm = ({ user, company }: ContactFormProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  // Default form state for inputs.
  const initial_contact_details: ContactFormDetails = {
    first_name: user ? user.first_name : '',
    last_name: user ? user.last_name : '',
    company: company ? company.name : '',
    email: user ? user.email : '',
    phone_number: user ? user.phone : '',
    application_area: '',
    message: ''
  };

  const [contactDetails, setContactDetails] = useState<ContactFormDetails>(initial_contact_details);
  const [sendLoading, setSendLoading] = useState<boolean>(false);

  const navigate: NavigateFunction = useNavigate();
  const authenticated: boolean = !!user && !!company;

  const valid_inputs: boolean =
    !!contactDetails.first_name &&
    !!contactDetails.last_name &&
    !!contactDetails.company &&
    !!contactDetails.email &&
    validateEmail(contactDetails.email) &&
    !!contactDetails.application_area &&
    (!authenticated || !!contactDetails.message);

  const application_area_options: Array<string> = [
    'Sustainability',
    'Utility Management',
    'Asset Management',
    'Multiple platform modules'
  ];

  application_area_options.push(
    ...(authenticated ? ['Other'] : ['Existing user support', 'Other'])
  );

  const handleSend = async (): Promise<void> => {
    const request_body: ContactRequestBody = {
      first_name: contactDetails.first_name,
      last_name: contactDetails.last_name,
      company: contactDetails.company,
      application_area: contactDetails.application_area,
      message: contactDetails.message,
      email: contactDetails.email,
      phone_number: contactDetails.phone_number,
      request_inbox: authenticated
        ? 'platformsupport@energypartners.co.za'
        : 'sustainability@energypartners.co.za'
    };
    try {
      setSendLoading(true);
      const response: void | AxiosResponse = await axios({
        method: 'post',
        url: `${API_ENDPOINT}/send_contact_request`,
        data: request_body
      }).catch((err: unknown) => {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: ContactForm failed on an unknown error while calling handleSend.',
            {
              request_body: request_body
            },
            tracking_id
          )
        );
      });
      if (response && response.status < 300) {
        navigate('/thank-you');
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ContactForm failed on an unknown error while calling handleSend.',
          {
            request_body: request_body
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to send contact request. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setSendLoading(false);
    }
  };

  const handleContactDetailsChange = (field: keyof ContactFormDetails, value: string): void => {
    setContactDetails({
      ...contactDetails,
      [field]: value
    });
  };

  return (
    <BorderContainer>
      <Grid container spacing={{ xs: 5, md: 12 }} sx={{ p: { xs: 2, md: 4 } }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Grid container spacing={5}>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-first-name"
                label="First Name"
                value={contactDetails.first_name}
                handleInputChange={(value: string) =>
                  handleContactDetailsChange('first_name', value)
                }
                options={{
                  variant: 'standard',
                  required: true
                }}
              />
            </Grid>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-last-name"
                label="Last Name"
                value={contactDetails.last_name}
                handleInputChange={(value: string) =>
                  handleContactDetailsChange('last_name', value)
                }
                options={{
                  variant: 'standard',
                  required: true
                }}
              />
            </Grid>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-company"
                label="Company Name"
                value={contactDetails.company}
                handleInputChange={(value: string) => handleContactDetailsChange('company', value)}
                options={{
                  variant: 'standard',
                  required: true
                }}
              />
            </Grid>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-email"
                label="Email"
                value={contactDetails.email}
                handleInputChange={(value: string) => handleContactDetailsChange('email', value)}
                options={{
                  variant: 'standard',
                  required: true,
                  ...(!validateEmail(contactDetails.email) && contactDetails.email
                    ? {
                        error: true,
                        helper_text: 'Invalid Email'
                      }
                    : {})
                }}
              />
            </Grid>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-phone"
                label="Contact Number"
                value={contactDetails.phone_number}
                handleInputChange={(value: string) =>
                  handleContactDetailsChange('phone_number', value)
                }
                options={{
                  variant: 'standard'
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={5}>
            <Grid size={12}>
              <ContactFormSelect
                id="interest-select-label"
                label={authenticated ? 'Application Module' : "I'm Interested In..."}
                value={contactDetails.application_area}
                choices={application_area_options}
                handleInputChange={(value: string) =>
                  handleContactDetailsChange('application_area', value)
                }
                options={{
                  required: true,
                  variant: 'standard'
                }}
              />
            </Grid>
            <Grid size={12}>
              <ContactFormTextInput
                id="text-message"
                label="Message"
                value={contactDetails.message}
                handleInputChange={(value: string) => handleContactDetailsChange('message', value)}
                options={{
                  multiline: true,
                  rows: 3,
                  required: authenticated
                }}
              />
            </Grid>
            <Grid size={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <PrimaryButton
                loading={sendLoading}
                sx={{
                  width: '60%',
                  marginTop: 5
                }}
                onClick={async () => await handleSend()}
                disabled={!valid_inputs}
              >
                Send
              </PrimaryButton>
              {!valid_inputs && (
                <Typography variant="caption" color="error" paddingTop={0.5}>
                  Missing required* fields
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </BorderContainer>
  );
};

export default ContactForm;
